import { useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useState } from 'react';

import {
  getProdutosAdminControllerListarQueryKey,
  useProdutosAdminControllerAtualizar,
  useProdutosAdminControllerCriar,
} from '@/api/generated/admin-produtos-de-parceiros/admin-produtos-de-parceiros';
import type { ParceiroAdminResponse, ProdutoAdminResponse } from '@/api/generated/model';
import { CriarProdutoDtoCategoria } from '@/api/generated/model';
import { ApiError } from '@/api/http-client';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/select';
import { toast } from '@/shared/ui/sonner';
import { Textarea } from '@/shared/ui/textarea';

/**
 * Categoria é opcional, e `<Select>` do Radix não aceita `value=""` — daí o
 * valor sentinela, traduzido para `null` no envio.
 */
const SEM_CATEGORIA = 'sem';

const CATEGORIAS = Object.values(CriarProdutoDtoCategoria);

export function ProdutoFormDialogo({
  aberto,
  aoFechar,
  parceiros,
  produto,
}: {
  aberto: boolean;
  aoFechar: () => void;
  parceiros: readonly ParceiroAdminResponse[];
  produto?: ProdutoAdminResponse;
}) {
  const queryClient = useQueryClient();
  const criar = useProdutosAdminControllerCriar();
  const atualizar = useProdutosAdminControllerAtualizar();

  const [parceiroId, setParceiroId] = useState(produto?.parceiroId ?? '');
  const [nome, setNome] = useState(produto?.nome ?? '');
  const [categoria, setCategoria] = useState<string>(produto?.categoria ?? SEM_CATEGORIA);
  const [descricao, setDescricao] = useState(produto?.descricao ?? '');
  const [erros, setErros] = useState<Record<string, string[]>>({});

  const salvando = criar.isPending || atualizar.isPending;

  function salvar(): void {
    setErros({});

    const comuns = {
      nome: nome.trim(),
      categoria: categoria === SEM_CATEGORIA ? null : (categoria as CriarProdutoDtoCategoria),
      descricao: descricao.trim() || null,
    };

    const aoDarCerto = () => {
      void queryClient.invalidateQueries({
        queryKey: getProdutosAdminControllerListarQueryKey(),
      });
      toast.success(produto ? 'Produto atualizado' : 'Produto cadastrado');
      aoFechar();
    };

    const aoFalhar = (erro: unknown) => {
      if (erro instanceof ApiError && erro.detalhes) {
        setErros(erro.detalhes);
        return;
      }
      toast.error(erro instanceof ApiError ? erro.message : 'Não foi possível salvar');
    };

    if (produto) {
      atualizar.mutate({ id: produto.id, data: comuns }, { onSuccess: aoDarCerto, onError: aoFalhar });
    } else {
      criar.mutate({ data: { parceiroId, ...comuns } }, { onSuccess: aoDarCerto, onError: aoFalhar });
    }
  }

  return (
    <Dialog open={aberto} onOpenChange={(v) => !v && aoFechar()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{produto ? 'Editar produto' : 'Novo produto'}</DialogTitle>
          <DialogDescription>
            {produto
              ? 'O parceiro dono não muda — mover o produto levaria junto o histórico de importações.'
              : 'A imagem 1:1 é enviada depois, pelo menu da listagem.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {!produto && (
            <Campo rotulo="Parceiro" erro={erros.parceiroId}>
              <Select value={parceiroId} onValueChange={setParceiroId}>
                <SelectTrigger>
                  <SelectValue placeholder="Escolha o parceiro" />
                </SelectTrigger>
                <SelectContent>
                  {parceiros.map((parceiro) => (
                    <SelectItem key={parceiro.id} value={parceiro.id}>
                      {parceiro.nome}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Campo>
          )}

          <Campo rotulo="Nome" erro={erros.nome}>
            <Input value={nome} onChange={(e) => setNome(e.target.value)} maxLength={200} />
          </Campo>

          <Campo rotulo="Categoria" erro={erros.categoria}>
            <Select value={categoria} onValueChange={setCategoria}>
              <SelectTrigger>
                <SelectValue placeholder="Sem categoria" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={SEM_CATEGORIA}>Sem categoria</SelectItem>
                {CATEGORIAS.map((opcao) => (
                  <SelectItem key={opcao} value={opcao}>
                    {opcao}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Campo>

          <Campo rotulo="Descrição" erro={erros.descricao}>
            <Textarea
              value={descricao}
              onChange={(e) => setDescricao(e.target.value)}
              placeholder="2 a 3 linhas sobre o produto"
              rows={3}
            />
          </Campo>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={aoFechar} disabled={salvando}>
            Cancelar
          </Button>
          <Button
            onClick={salvar}
            disabled={salvando || (!produto && !parceiroId)}
            className="bg-brand text-brand-foreground hover:bg-brand/90"
          >
            {salvando && <Loader2 className="w-4 h-4 animate-spin" />}
            {produto ? 'Salvar' : 'Cadastrar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Campo({
  rotulo,
  erro,
  children,
}: {
  rotulo: string;
  erro?: string[];
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm font-medium">{rotulo}</Label>
      {children}
      {erro && erro.length > 0 && <p className="text-xs text-destructive">{erro[0]}</p>}
    </div>
  );
}
